import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  TrendingUp, TrendingDown, DollarSign,
  Wallet, Building2, Users, Calendar, FileText,
  BarChart3, Eye, Package, Truck, ClipboardList,
  IndianRupee,
} from 'lucide-react';
import pb from '../lib/pocketbaseClient';
import { formatDate } from '../utils/dateHelpers';

// ─── Currency formatter – full number, ₹ symbol ──────────────
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialRequests, setMaterialRequests] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalType, setModalType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Dropdown data
  const [vendors, setVendors] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchProjectDetails();
    fetchDropdownData();
  }, [id]);

  const fetchDropdownData = async () => {
    try {
      const [vendorsRes, partnersRes] = await Promise.all([
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('partners').getFullList({ sort: 'name' }),
      ]);
      setVendors(vendorsRes);
      setPartners(partnersRes);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProjectDetails = async () => {
    setLoading(true);
    try {
      const projectData = await pb.collection('accounts_projects').getOne(id, { expand: 'clientId' });
      setProject(projectData);

      const [
        expensesData,
        vendorPaymentsData,
        clientPaymentsData,
        investmentsData,
        materialsData,
        requestsData,
        posData,
      ] = await Promise.all([
        pb.collection('project_expenses').getFullList({ filter: `project="${id}"`, sort: '-date' }),
        pb.collection('vendor_payments').getFullList({ filter: `project="${id}"`, sort: '-paymentDate', expand: 'vendor' }),
        pb.collection('client_payments').getFullList({ filter: `project="${id}"`, sort: '-paymentDate' }),
        pb.collection('investments').getFullList({ filter: `project="${id}"`, sort: '-date', expand: 'partner' }),
        pb.collection('material_transactions').getFullList({
          filter: `project="${id}" && type="issue"`,
          sort: '-date',
          expand: 'material,uom',
        }),
        pb.collection('material_requests').getFullList({
          filter: `project="${id}"`,
          sort: '-request_date',
          expand: 'requested_by,approved_by',
        }),
        pb.collection('material_purchase_orders').getFullList({
          filter: `project="${id}"`,
          sort: '-order_date',
          expand: 'supplier,created_by',
        }),
      ]);

      setExpenses(expensesData);
      setVendorPayments(vendorPaymentsData);
      setClientPayments(clientPaymentsData);
      setInvestments(investmentsData);
      setMaterials(materialsData);
      setMaterialRequests(requestsData);
      setPurchaseOrders(posData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Computations (new logic) ──────────────────────────────
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalVendorPayments = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalClientPayments = clientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalDeposits = investments.filter(i => i.type === 'deposit').reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalWithdrawals = investments.filter(i => i.type === 'withdrawal').reduce((sum, i) => sum + (i.amount || 0), 0);

  const netInvestedCapital = totalDeposits - totalWithdrawals;
  const collectedAmount = totalClientPayments + netInvestedCapital;
  const projectCost = totalExpenses + totalVendorPayments;
  const balance = collectedAmount - projectCost;
  const profitLoss = balance - netInvestedCapital; // = clientPayments - projectCost

  // Material summary
  const materialCost = materials.reduce((sum, t) => sum + (t.total_cost || 0), 0);
  const materialQuantity = materials.reduce((sum, t) => sum + (t.quantity || 0), 0);

  // Investor summary
  const investorSummary = {};
  investments.forEach(i => {
    if (!investorSummary[i.partner]) {
      const partner = partners.find(p => p.id === i.partner);
      investorSummary[i.partner] = {
        name: partner?.name || 'Unknown',
        deposits: 0,
        withdrawals: 0,
        net: 0,
      };
    }
    if (i.type === 'deposit') {
      investorSummary[i.partner].deposits += i.amount || 0;
    } else {
      investorSummary[i.partner].withdrawals += i.amount || 0;
    }
    investorSummary[i.partner].net = investorSummary[i.partner].deposits - investorSummary[i.partner].withdrawals;
  });

  // ─── Modal functions (unchanged) ────────────────────────────
  const openModal = (type) => {
    setModalType(type);
    setModalForm({});
    setModalError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
    setModalForm({});
    setModalLoading(false);
    setModalError('');
  };

  const handleModalChange = (e) => {
    const { name, value, files } = e.target;
    setModalForm(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');

    try {
      let data = {};
      let collection = '';

      switch (modalType) {
        case 'expense':
          collection = 'project_expenses';
          data = {
            project: id,
            category: modalForm.category || 'material',
            description: modalForm.description,
            amount: parseFloat(modalForm.amount) || 0,
            date: modalForm.date,
            paidFrom: modalForm.paidFrom || 'office_fund',
          };
          if (modalForm.receiptImage) {
            const formData = new FormData();
            Object.keys(data).forEach(k => formData.append(k, data[k]));
            formData.append('receiptImage', modalForm.receiptImage);
            await pb.collection(collection).create(formData);
          } else {
            await pb.collection(collection).create(data);
          }
          break;
        case 'vendorPayment':
          collection = 'vendor_payments';
          data = {
            project: id,
            vendor: modalForm.vendor,
            amount: parseFloat(modalForm.amount) || 0,
            paymentDate: modalForm.paymentDate,
            paymentType: modalForm.paymentType || 'weekly',
            paymentMethod: modalForm.paymentMethod || 'cash',
            paidFrom: modalForm.paidFrom || 'office_fund',
            transactionId: modalForm.transactionId || null,
            note: modalForm.note || null,
          };
          await pb.collection(collection).create(data);
          break;
        case 'clientPayment':
          collection = 'client_payments';
          data = {
            project: id,
            amount: parseFloat(modalForm.amount) || 0,
            paymentDate: modalForm.paymentDate,
            method: modalForm.method || 'bank_transfer',
            note: modalForm.note || null,
          };
          await pb.collection(collection).create(data);
          break;
        case 'investment':
          collection = 'investments';
          data = {
            partner: modalForm.partner,
            project: id,
            amount: parseFloat(modalForm.amount) || 0,
            type: modalForm.type || 'deposit',
            date: modalForm.date,
            note: modalForm.note || null,
            reference: modalForm.reference || null,
          };
          await pb.collection(collection).create(data);
          break;
        default:
          throw new Error('Invalid modal type');
      }

      closeModal();
      fetchProjectDetails();
    } catch (err) {
      let msg = err.message || 'Failed to save';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join('; ');
        msg += ` (${fields})`;
      }
      setModalError(msg);
      setModalLoading(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-slate-800">Project not found</h2>
        <Link to="/projects" className="text-amber-600 hover:underline mt-4 block">← Back to Projects</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 bg-slate-50/50 min-h-screen">
      {/* ─── Header ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Link to="/projects" className="text-sm text-amber-600 hover:underline">← Back to Projects</Link>
          <h1 className="text-2xl font-bold text-slate-800 mt-1">{project.name}</h1>
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <span>{project.type}</span>
            <Badge variant="outline">{project.status}</Badge>
            <span>{project.location || 'No location'}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="bg-amber-500 hover:bg-amber-600 text-sm" onClick={() => openModal('expense')}>
            + Add Expense
          </Button>
          <Button variant="outline" className="text-sm" onClick={() => openModal('vendorPayment')}>
            + Vendor Payment
          </Button>
          <Button variant="outline" className="text-sm" onClick={() => openModal('clientPayment')}>
            + Payment Received
          </Button>
          <Button variant="outline" className="text-sm" onClick={() => openModal('investment')}>
            + Investment
          </Button>
          <Link to={`/projects/${project.id}/edit`}>
            <Button variant="ghost" size="sm">Edit</Button>
          </Link>
        </div>
      </div>

      {/* ─── Project Overview Cards (new metrics) ────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Collected Amount</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(collectedAmount)}</p>
                <p className="text-xs text-slate-400 mt-1">Revenue + Net Invested Capital</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Project Cost</p>
                <p className="text-2xl font-bold text-rose-600">{formatCurrency(projectCost)}</p>
                <p className="text-xs text-slate-400 mt-1">Expenses + Vendor Payments</p>
              </div>
              <TrendingDown className="w-8 h-8 text-rose-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Balance</p>
                <p className={`text-2xl font-bold ${balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(balance)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Collected Amount – Cost</p>
              </div>
              <Wallet className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 shadow-sm ${profitLoss >= 0 ? 'border-l-green-600' : 'border-l-red-600'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Profit / Loss</p>
                <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {profitLoss >= 0 ? '+' : ''}{formatCurrency(profitLoss)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Balance – Net Invested Capital</p>
              </div>
              {profitLoss >= 0 ? (
                <TrendingUp className="w-8 h-8 text-green-500" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Fund Flow & Investor Summary ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-500" /> Fund Flow
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm text-slate-600">💰 Total Client Payments</span>
              <span className="font-semibold text-emerald-600">{formatCurrency(totalClientPayments)}</span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm text-slate-600">🏦 Net Invested Capital</span>
              <span className="font-semibold text-amber-600">{formatCurrency(netInvestedCapital)}</span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm text-slate-600">📉 Total Withdrawals</span>
              <span className="font-semibold text-red-600">{formatCurrency(totalWithdrawals)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t-2 border-slate-200">
              <span className="text-sm font-bold text-slate-700">Collected Amount</span>
              <span className="font-bold text-slate-800">{formatCurrency(collectedAmount)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" /> Investor Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(investorSummary).length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No investors</p>
            ) : (
              <div className="space-y-3">
                {Object.values(investorSummary).map((inv, idx) => (
                  <div key={idx} className="border-b pb-2 last:border-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{inv.name}</span>
                      <Badge variant={inv.net >= 0 ? 'success' : 'destructive'}>
                        {inv.net >= 0 ? 'Investor' : 'Due'}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-slate-500">Deposited</span>
                      <span className="text-emerald-600">{formatCurrency(inv.deposits)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Returned</span>
                      <span className="text-red-600">{formatCurrency(inv.withdrawals)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t">
                      <span>Net Balance</span>
                      <span className={inv.net >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {formatCurrency(inv.net)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Project Materials ────────────────────────────────── */}
      <Card className="border-t-4 border-t-amber-400">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-500" /> Project Materials
          </CardTitle>
        </CardHeader>
        <CardContent>
          {materials.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No materials consumed in this project.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Total Items Used</p>
                  <p className="text-lg font-bold">{materialQuantity}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Total Cost</p>
                  <p className="text-lg font-bold">{formatCurrency(materialCost)}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Material</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">UOM</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Qty</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Unit Cost</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Total</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {materials.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2 font-medium">{t.expand?.material?.name || 'N/A'}</td>
                        <td className="px-4 py-2">{t.expand?.uom?.symbol || '—'}</td>
                        <td className="px-4 py-2 text-right font-mono">{t.quantity}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(t.unit_cost || 0)}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(t.total_cost || 0)}</td>
                        <td className="px-4 py-2">{formatDate(t.date)}</td>
                        <td className="px-4 py-2">{t.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ─── Material Requests ────────────────────────────────── */}
      <Card className="border-t-4 border-t-blue-400">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-500" /> Material Requests
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {materialRequests.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No material requests for this project.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Request #</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Requested By</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {materialRequests.map(req => (
                  <tr key={req.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2 font-mono text-xs">{req.id.slice(0, 8)}</td>
                    <td className="px-4 py-2">{formatDate(req.request_date)}</td>
                    <td className="px-4 py-2">
                      <Badge variant={req.status === 'Approved' ? 'success' : req.status === 'Pending' ? 'warning' : 'secondary'}>
                        {req.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">{req.expand?.requested_by?.name || 'N/A'}</td>
                    <td className="px-4 py-2 text-center">
                      <Link to={`/materials/requests/${req.id}`} className="text-blue-600 hover:underline text-sm">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ─── Purchase Orders ──────────────────────────────────── */}
      <Card className="border-t-4 border-t-green-400">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Truck className="w-5 h-5 text-green-500" /> Purchase Orders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {purchaseOrders.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No purchase orders for this project.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">PO #</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Supplier</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Total</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {purchaseOrders.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2 font-mono text-xs">{po.po_number || po.id.slice(0, 8)}</td>
                    <td className="px-4 py-2">{po.expand?.supplier?.companyName || po.expand?.supplier?.name || 'N/A'}</td>
                    <td className="px-4 py-2">{formatDate(po.order_date)}</td>
                    <td className="px-4 py-2">
                      <Badge variant={po.status === 'Received' ? 'success' : po.status === 'Sent' ? 'info' : 'secondary'}>
                        {po.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{formatCurrency(po.total_amount || 0)}</td>
                    <td className="px-4 py-2 text-center">
                      <Link to={`/materials/purchase-orders/${po.id}`} className="text-blue-600 hover:underline text-sm">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ─── Transaction Tables ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-t-4 border-t-amber-400">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-amber-500" /> Project Expenses
          </CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Category</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">From</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {expenses.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-4 text-center text-slate-500">No expenses</td></tr>
                  ) : (
                    expenses.map(e => (
                      <tr key={e.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2">{formatDate(e.date)}</td>
                        <td className="px-4 py-2"><Badge className="bg-blue-100 text-blue-700">{e.category}</Badge></td>
                        <td className="px-4 py-2 text-xs">
                          {e.paidFrom === 'office_fund' ? '🏦 Office' : partners.find(p => p.id === e.paidFrom)?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(e.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50 border-t font-semibold">
                  <tr><td colSpan="3" className="px-4 py-2 text-right">Total</td><td className="px-4 py-2 text-right">{formatCurrency(totalExpenses)}</td></tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-blue-400">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-500" /> Vendor Payments
          </CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Vendor</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">From</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {vendorPayments.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-4 text-center text-slate-500">No vendor payments</td></tr>
                  ) : (
                    vendorPayments.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2">{formatDate(p.paymentDate)}</td>
                        <td className="px-4 py-2">{p.expand?.vendor?.name || 'N/A'}</td>
                        <td className="px-4 py-2 text-xs">
                          {p.paidFrom === 'office_fund' ? '🏦 Office' : partners.find(pr => pr.id === p.paidFrom)?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(p.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50 border-t font-semibold">
                  <tr><td colSpan="3" className="px-4 py-2 text-right">Total</td><td className="px-4 py-2 text-right">{formatCurrency(totalVendorPayments)}</td></tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Client Payments Table ────────────────────────────── */}
      <Card className="border-t-4 border-t-emerald-400">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" /> Client Payments Received
        </CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Method</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clientPayments.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-4 text-center text-slate-500">No client payments</td></tr>
                ) : (
                  clientPayments.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-2">{formatDate(p.paymentDate)}</td>
                      <td className="px-4 py-2"><Badge className="bg-green-100 text-green-700">{p.method}</Badge></td>
                      <td className="px-4 py-2">{p.note || '-'}</td>
                      <td className="px-4 py-2 text-right font-mono text-emerald-600">{formatCurrency(p.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t font-semibold">
                <tr><td colSpan="3" className="px-4 py-2 text-right">Total Revenue</td><td className="px-4 py-2 text-right">{formatCurrency(totalClientPayments)}</td></tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ─── Investments Table ──────────────────────────────────── */}
      <Card className="border-t-4 border-t-purple-400">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-purple-500" /> Investments
        </CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Partner</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {investments.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-4 text-center text-slate-500">No investments</td></tr>
                ) : (
                  investments.map(inv => {
                    const partner = partners.find(p => p.id === inv.partner);
                    return (
                      <tr key={inv.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2">{formatDate(inv.date)}</td>
                        <td className="px-4 py-2">{partner?.name || 'N/A'}</td>
                        <td className="px-4 py-2">
                          <Badge variant={inv.type === 'deposit' ? 'success' : 'destructive'}>
                            {inv.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2">{inv.note || '-'}</td>
                        <td className={`px-4 py-2 text-right font-mono ${inv.type === 'deposit' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {inv.type === 'deposit' ? '+' : '-'}{formatCurrency(inv.amount)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t font-semibold">
                <tr>
                  <td colSpan="4" className="px-4 py-2 text-right">Net Invested Capital</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(netInvestedCapital)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ─── Modal ────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {modalType === 'expense' && '➕ Add Expense'}
              {modalType === 'vendorPayment' && '➕ Add Vendor Payment'}
              {modalType === 'clientPayment' && '➕ Payment Received'}
              {modalType === 'investment' && '➕ Add Fund / Investment'}
            </h2>
            <p className="text-sm text-slate-500 mb-4">For project: {project.name}</p>

            {modalError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
                {modalError}
              </div>
            )}

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {modalType === 'expense' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                      <select name="category" value={modalForm.category || 'material'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="material">Material</option>
                        <option value="transport">Transport</option>
                        <option value="equipment">Equipment</option>
                        <option value="labour">Labour</option>
                        <option value="utility">Utility</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Paid From *</label>
                      <select name="paidFrom" value={modalForm.paidFrom || 'office_fund'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="office_fund">🏦 Office Fund</option>
                        {partners.map(p => <option key={p.id} value={p.id}>🤝 {p.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                    <Input name="description" value={modalForm.description || ''} onChange={handleModalChange} required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label>
                      <Input name="amount" type="number" value={modalForm.amount || ''} onChange={handleModalChange} required step="0.01" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                      <Input name="date" type="date" value={modalForm.date || ''} onChange={handleModalChange} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Receipt Image</label>
                    <Input name="receiptImage" type="file" onChange={handleModalChange} accept="image/*" />
                  </div>
                </>
              )}

              {modalType === 'vendorPayment' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Vendor *</label>
                      <select name="vendor" value={modalForm.vendor || ''} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="">Select Vendor</option>
                        {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Payment Type *</label>
                      <select name="paymentType" value={modalForm.paymentType || 'weekly'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="final">Final</option>
                        <option value="advance">Advance</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method *</label>
                      <select name="paymentMethod" value={modalForm.paymentMethod || 'cash'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Paid From *</label>
                      <select name="paidFrom" value={modalForm.paidFrom || 'office_fund'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="office_fund">🏦 Office Fund</option>
                        {partners.map(p => <option key={p.id} value={p.id}>🤝 {p.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label>
                      <Input name="amount" type="number" value={modalForm.amount || ''} onChange={handleModalChange} required step="0.01" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label>
                      <Input name="paymentDate" type="date" value={modalForm.paymentDate || ''} onChange={handleModalChange} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Transaction ID</label>
                    <Input name="transactionId" value={modalForm.transactionId || ''} onChange={handleModalChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                    <Input name="note" value={modalForm.note || ''} onChange={handleModalChange} />
                  </div>
                </>
              )}

              {modalType === 'clientPayment' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label>
                      <Input name="amount" type="number" value={modalForm.amount || ''} onChange={handleModalChange} required step="0.01" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label>
                      <Input name="paymentDate" type="date" value={modalForm.paymentDate || ''} onChange={handleModalChange} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method *</label>
                    <select name="method" value={modalForm.method || 'bank_transfer'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="upi">UPI</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                    <Input name="note" value={modalForm.note || ''} onChange={handleModalChange} />
                  </div>
                </>
              )}

              {modalType === 'investment' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Partner *</label>
                      <select name="partner" value={modalForm.partner || ''} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="">Select Partner</option>
                        {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                      <select name="type" value={modalForm.type || 'deposit'} onChange={handleModalChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                        <option value="deposit">Deposit</option>
                        <option value="withdrawal">Withdrawal</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label>
                      <Input name="amount" type="number" value={modalForm.amount || ''} onChange={handleModalChange} required step="0.01" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                      <Input name="date" type="date" value={modalForm.date || ''} onChange={handleModalChange} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                    <Input name="note" value={modalForm.note || ''} onChange={handleModalChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                    <Input name="reference" value={modalForm.reference || ''} onChange={handleModalChange} />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={modalLoading}>
                  {modalLoading ? 'Saving...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}